import { UUID, hooks, intl, logger, ui } from '@beelday/common'
import { RootState } from 'common/redux'
import { ArchivedActivityCard } from 'features/script'
import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { ErrorWithStreams } from 'room/common/error-with-streams'
import { useAssertedJoinedRoomAddress } from 'room/common/use-joined-room-address'
import { navigateToRecaps } from 'workflow/navigate-to-recaps'
import ConfiguredKnowledgeCheckStage from './configured-knowledge-check-stage'
import {
	KnowledgeCheckReview,
	KnowledgeCheckTrainerLayout,
} from './knowledge-check'
import KnowledgeCheckReviewPopup from './knowledge-check-review-popup'
import { knowledgeCheckAnswersVersion } from './knowledge-checks-redux'
import KnowledgeCheckTrainerGridView from './presentation/knowledge-check-trainer-grid-view'
import useKnowledgeChecks from './use-knowledge-checks'
import useStats from './use-stats'

// Answers often arrive in bursts: refetch the open results once they settle,
// but at least once a second while they keep coming
const REVIEW_REFRESH_DELAY_MS = 300
const REVIEW_REFRESH_MAX_WAIT_MS = 1000

const ConfiguredKnowledgeCheckTrainer: FC<{
	knowledgeCheckId: UUID
	layout: KnowledgeCheckTrainerLayout
}> = ({ knowledgeCheckId, layout }) => {
	const roomAddress = useAssertedJoinedRoomAddress()
	const { getKnowledgeCheck, getReview, finishKnowledgeCheck } =
		useKnowledgeChecks()

	const confirm = ui.useConfirm()
	const training = useSelector((state: RootState) => state.sessionInfo.training)
	const trainerProfile = useSelector(
		(state: RootState) =>
			state.users.all[
				training?.upperEchelon.userId ? training?.upperEchelon.userId : ''
			]
	)

	const stats = useStats(roomAddress, knowledgeCheckId)

	const [review, setReview] = useState<KnowledgeCheckReview>()
	const [showPartialResults, setShowPartialResults] = useState<boolean>(false)
	const answersVersion = useSelector(knowledgeCheckAnswersVersion)
	const currentVersion = useRef(answersVersion)
	currentVersion.current = answersVersion
	const latestReviewRequest = useRef(0)
	const loadedVersion = useRef<number>()
	const lastLoadAt = useRef(0)

	const loadReview = useCallback(() => {
		const request = ++latestReviewRequest.current
		loadedVersion.current = currentVersion.current
		lastLoadAt.current = Date.now()
		getReview(roomAddress, knowledgeCheckId)
			.then(fetched => {
				// a slower, older response must not replace a newer one
				if (request === latestReviewRequest.current) setReview(fetched)
			})
			.catch(e => logger.error('Could not load knowledge check results', e))
	}, [getReview, roomAddress, knowledgeCheckId])

	const loadKnowledgeCheck = useCallback(
		() => getKnowledgeCheck(roomAddress, knowledgeCheckId),
		[getKnowledgeCheck, roomAddress, knowledgeCheckId]
	)

	const {
		result: knowledgeCheck,
		error,
		reload,
	} = hooks.useLoading(loadKnowledgeCheck)

	// Loaded up front so "Show more" opens at once, and again when it opens
	useEffect(() => {
		if (knowledgeCheckId) loadReview()
	}, [knowledgeCheckId, loadReview])
	useEffect(() => {
		if (showPartialResults) loadReview()
	}, [showPartialResults, loadReview])

	// Keep the open results drawer in step with answers coming in
	useEffect(() => {
		if (!showPartialResults || answersVersion === loadedVersion.current) return
		const sinceLastLoad = Date.now() - lastLoadAt.current
		const wait = Math.max(
			0,
			Math.min(
				REVIEW_REFRESH_DELAY_MS,
				REVIEW_REFRESH_MAX_WAIT_MS - sinceLastLoad
			)
		)
		const timer = setTimeout(loadReview, wait)
		return () => clearTimeout(timer)
	}, [answersVersion, loadReview, showPartialResults])

	const onFinish = () => {
		if (knowledgeCheckId) {
			confirm
				.generic(
					<intl.Translate>
						knowledge_check.question.finish.popup.title
					</intl.Translate>,
					<intl.Translate>
						knowledge_check.question.finish.popup.desc.confirm
					</intl.Translate>,
					<intl.Translate>
						knowledge_check.question.finish.popup.confirm
					</intl.Translate>,
					<intl.Translate>
						knowledge_check.question.finish.popup.cancel
					</intl.Translate>
				)
				.then(() => finishKnowledgeCheck(roomAddress, knowledgeCheckId))
		}
	}

	if (error) {
		return (
			<ErrorWithStreams retry={reload}>
				<intl.Translate>knowledge_check.error.load</intl.Translate>
			</ErrorWithStreams>
		)
	}

	if (knowledgeCheck?.doneInPreviousSession) {
		return (
			<ArchivedActivityCard
				title={knowledgeCheck.name}
				toolType="KNOWLEDGE_CHECK"
				representativeQuestion={knowledgeCheck.questions[0]?.question}
				isTrainer={true}
				onSeeInRecaps={
					training
						? () =>
								navigateToRecaps({ trainingId: training.id, isTrainer: true })
						: undefined
				}
			/>
		)
	}

	return (
		<>
			{layout === 'two-pane' ? (
				<ConfiguredKnowledgeCheckStage
					userProfile={trainerProfile}
					title={knowledgeCheck?.name || ''}
					maxDurationInSeconds={knowledgeCheck?.maxDurationInSeconds}
					startTime={knowledgeCheck?.startTime}
					stats={stats}
					onFinish={onFinish}
					onShowResults={() => setShowPartialResults(true)}
				/>
			) : (
				<KnowledgeCheckTrainerGridView
					userProfile={trainerProfile}
					title={knowledgeCheck?.name || ''}
					maxDurationInSeconds={knowledgeCheck?.maxDurationInSeconds}
					startTime={knowledgeCheck?.startTime}
					stats={stats}
					onFinish={onFinish}
					onShowReview={() => setShowPartialResults(true)}
				/>
			)}
			{showPartialResults && review ? (
				<KnowledgeCheckReviewPopup
					onClose={() => setShowPartialResults(false)}
					review={review}
					useCorrectAnswers={knowledgeCheck?.useCorrectAnswers || false}
					stats={stats}
				/>
			) : null}
		</>
	)
}

export default ConfiguredKnowledgeCheckTrainer
